import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {DateTime} from 'luxon';
import {MessageBusService} from '../message-bus.service';
import {SessionService} from '../authentication/session-service.service';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {ApplicationSettingsService} from '../app-settings/application-settings.service';
import {GetLoadedStreams} from '../shell-communication/shell-operations-protocol';
import {QuoteGeneratorLoadedStreamDto, ServerTimeMessageDto} from '../shell-communication/shell-dto-protocol';
import {isVoid} from '../utils';

/** The QG cursor messages' payload (anonymous bus payloads; the publisher is QuoteGeneratorService). */
interface QgCursorDatePayload {
    ticker: string;
    cursorDate: string;
}

/**
 * CR-20260916-005 — THE ONE "WHAT IS TODAY" FOR A PANEL.
 *
 * "Today" is never the browser clock. It is:
 *
 *  1. In a Quote Generator shell: the REPLAY CURSOR of the stream that is in History mode — the
 *     underlying's own stream when it replays, else whichever stream does (the trader replays SPX
 *     and runs a SPY portfolio; owner smoke 2026-09-16). The cursor is an Eastern wall-clock
 *     timestamp; its day part is the day key.
 *  2. Otherwise: the last ServerTimeMessageDto, converted exactly as the status-bar clock and the
 *     APG's readTodayKey convert it — an emulated "fake" time is a wall-clock value in the shell's
 *     zone, a real time is shown in the default zone.
 *
 * WHY A SERVICE AND NOT THE BUS REQUEST ALONE. The QG cursor reaches panels today through
 * 'Qg.CursorDateRequest', answered by QuoteGeneratorService — which only exists once the QG PANEL
 * has been opened in the session. With the panel closed nobody answers, and every consumer fell
 * back to the browser clock without noticing. Owner ruling 2 (2026-09-16): Maven must work with
 * the QG panel closed. So when the bus is silent in a QG shell, this service asks the shell
 * itself (GetLoadedStreams carries each stream's mode and history cursor) and then BROADCASTS the
 * cursor on 'Qg.CursorDateChangedMessage' — the same message the QG panel would have sent — so the
 * option-chains service and every other consumer learn it the way they already know how to.
 *
 * Reads are synchronous over the last known value (`todayKey`), so a reader callback such as the
 * date questionnaire's anchor reader can use it; `refresh()` is the async resolve, awaited once
 * per portfolio load and re-run on its own when the cursor moves. `changed$` fires when the DAY
 * flips, so DTE-bearing views repaint.
 */
@Injectable({providedIn: 'root'})
export class ShellTodayService {

    constructor(
        private readonly _messageBus: MessageBusService,
        private readonly _sessionService: SessionService,
        private readonly _shellClient: ShellClientService,
        private readonly _applicationSettings: ApplicationSettingsService
    ) {
        // App-lifetime subscriptions, never torn down (same as the QG responder).
        this._messageBus.of<QgCursorDatePayload>('Qg.CursorDateChangedMessage')
            .subscribe(msg => this.onCursorDateChanged(msg.payload));
        this._messageBus.of<ServerTimeMessageDto>('ServerTimeMessageDto')
            .subscribe(msg => this.onServerTime(msg.payload));
        this._messageBus.of<{stream: string, mode: string}>('Qg.StreamModeChanged').subscribe(msg => {
            this.ensureSession(); ++this._revision;
            this._modes[msg.payload.stream] = msg.payload.mode;
            if (msg.payload.mode !== 'History') {
                delete this._cursorDayByTicker[msg.payload.stream];
                if (this._lastHistoryTicker === msg.payload.stream) { this._lastHistoryTicker = null; }
                this.publish(this.todayKey());
            }
            this.refresh(msg.payload.stream).catch(() => {});
        });
    }

    // The last cursor DAY seen per stream ticker, and the last History stream's ticker — so a
    // reader for an underlying with no replaying stream still gets the session's replay day.
    private readonly _cursorDayByTicker: {[ticker: string]: string} = {};
    private _lastHistoryTicker: string | null = null;
    private _lastServerTime: ServerTimeMessageDto | null = null;
    private _lastKey: string | null = null;
    private readonly _changed = new Subject<string | null>();
    private _login: any;
    private _revision = 0;
    private _modes: {[ticker: string]: string} = {};

    private ensureSession(): void {
        if (this._login === this._sessionService.loginResult) { return; }
        this._login = this._sessionService.loginResult; ++this._revision;
        Object.keys(this._cursorDayByTicker).forEach(key => delete this._cursorDayByTicker[key]);
        this._lastHistoryTicker = null; this._lastServerTime = null; this._lastKey = null; this._modes = {};
    }

    /** Fires with the new day key whenever the resolved day changes. */
    get changed$(): Observable<string | null> {
        return this._changed.asObservable();
    }

    get isQuoteGeneratorShell(): boolean {
        const login = this._sessionService.loginResult;
        return !!login && !!login.isQuoteGeneratorShell;
    }

    /**
     * Today as a 'yyyy-MM-dd' key from what is known NOW (no round trip). Null when nothing is
     * known yet — the caller then treats the day as unknown, never as the browser's.
     */
    todayKey(underlying?: string): string | null {
        this.ensureSession();
        if (this.isQuoteGeneratorShell) {
            const own = isVoid(underlying) ? undefined : this._cursorDayByTicker[underlying];
            if (!isVoid(own)) {
                return own;
            }
            if (this._lastHistoryTicker && !isVoid(this._cursorDayByTicker[this._lastHistoryTicker])) {
                return this._cursorDayByTicker[this._lastHistoryTicker];
            }
            return this.serverTodayKey();
        }
        return this.serverTodayKey();
    }

    /**
     * Resolve today, asking the shell when the bus has not told us yet. In a QG shell with the QG
     * panel closed this is the only way the cursor is ever learned. Returns the key (or null).
     */
    async refresh(underlying?: string): Promise<string | null> {
        this.ensureSession();
        const revision = this._revision, login = this._login;
        if (!this.isQuoteGeneratorShell) {
            return this.publish(this.serverTodayKey());
        }

        // The bus first: the QG panel, when open, answers synchronously by in-place mutation.
        const ticker = isVoid(underlying) ? this._lastHistoryTicker : underlying;
        if (!isVoid(ticker)) {
            const msg: QgCursorDatePayload = {ticker: ticker, cursorDate: undefined};
            this._messageBus.publish({topic: 'Qg.CursorDateRequest', payload: msg});
            if (!isVoid(msg.cursorDate) && (!this._modes[ticker] || this._modes[ticker] === 'History')) {
                this.remember(ticker, msg.cursorDate);
                return this.publish(this.todayKey(underlying));
            }
        }

        // Nobody answered: ask the shell itself.
        try {
            const streams = await this._shellClient.processQuery<QuoteGeneratorLoadedStreamDto[]>(new GetLoadedStreams());
            this.ensureSession();
            if (revision !== this._revision || login !== this._login) { return this.todayKey(underlying); }
            const history = (streams || []).filter(s => s && s.quoteMode === 'History' && !isVoid(s.historyCursor)
                && (!this._modes[s.ticker] || this._modes[s.ticker] === 'History'));
            Object.keys(this._cursorDayByTicker).forEach(key => delete this._cursorDayByTicker[key]);
            this._lastHistoryTicker = null;
            history.forEach(s => this.remember(s.ticker, s.historyCursor));
            // The underlying's own replaying stream first, else the first one replaying.
            const own = history.filter(s => s.ticker === underlying);
            const chosen = own.length > 0 ? own[0] : (history.length > 0 ? history[0] : null);
            if (chosen) {
                this.remember(chosen.ticker, chosen.historyCursor);
                // Tell the rest of the platform what the QG panel would have told it.
                this._messageBus.publish({
                    topic: 'Qg.CursorDateChangedMessage',
                    payload: {ticker: chosen.ticker, cursorDate: chosen.historyCursor}
                });
            }
        } catch (e) {
            console.error('ShellTodayService: GetLoadedStreams failed', e);
        }

        return this.publish(this.todayKey(underlying));
    }

    private serverTodayKey(): string | null {
        const st = this._lastServerTime;
        if (!st || !st.time) {
            return null;
        }
        let dt: DateTime;
        if (st.isFakeTime) {
            dt = DateTime.fromJSDate(st.time, {zone: 'UTC'}).setZone(st.timeZone || 'UTC', {keepLocalTime: true});
        } else {
            dt = DateTime.fromJSDate(st.time).setZone(this._applicationSettings.defaultTimezone || 'UTC');
        }
        return dt.isValid ? dt.toFormat('yyyy-LL-dd') : null;
    }

    private remember(ticker: string, cursorDate: string): void {
        this._cursorDayByTicker[ticker] = cursorDate.split('T')[0];
        this._lastHistoryTicker = ticker;
    }

    private onCursorDateChanged(payload: QgCursorDatePayload): void {
        this.ensureSession();
        if (!payload || isVoid(payload.ticker)) {
            return;
        }
        if (!isVoid(payload.cursorDate) && this._modes[payload.ticker] && this._modes[payload.ticker] !== 'History') { return; }
        const day = payload.cursorDate?.split('T')[0];
        const dayChanged = this._cursorDayByTicker[payload.ticker] !== day;
        if (dayChanged) { ++this._revision; }
        if (isVoid(payload.cursorDate)) {
            delete this._cursorDayByTicker[payload.ticker];
            if (this._lastHistoryTicker === payload.ticker) {
                this._lastHistoryTicker = null;
            }
        } else {
            this.remember(payload.ticker, payload.cursorDate);
        }
        const previousKey = this._lastKey;
        const key = this.publish(this.todayKey());
        if (dayChanged && key === previousKey) { this._changed.next(key); }
    }

    private onServerTime(payload: ServerTimeMessageDto): void {
        this.ensureSession();
        this._lastServerTime = payload || null;
        this.publish(this.todayKey());
    }

    // Emits only on a DAY flip: every intra-day tick yields the same key.
    private publish(key: string | null): string | null {
        if (key !== this._lastKey) {
            this._lastKey = key;
            this._changed.next(key);
        }
        return key;
    }
}
